import { Link } from "react-router-dom";
import { Truck, ShieldCheck, Banknote, ArrowRight } from "lucide-react";

const ICONS = [Truck, ShieldCheck, Banknote];
export const FONTS = ["default", "Playfair Display", "Montserrat", "Oswald", "Bebas Neue", "Poppins", "Merriweather", "Raleway"];
export const DESIGN = { show_text: true, bg_type: "image", bg_color: "#1e293b", bg_color2: "#0f172a", image_fit: "cover", overlay: 75,
  font: "default", size: "lg", text_color: "#ffffff", accent_color: "#f59e0b", align: "left", x: 0, y: 50, max_width: 60 };
const SIZES = {
  full: { sm: "text-2xl sm:text-3xl lg:text-4xl", md: "text-3xl sm:text-4xl lg:text-5xl", lg: "text-3xl sm:text-5xl lg:text-6xl", xl: "text-4xl sm:text-6xl lg:text-7xl" },
  mini: { sm: "text-sm", md: "text-base", lg: "text-lg", xl: "text-xl" },
};
export const imgSrc = (u) => (u?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const loaded = new Set();
export function loadFont(f) {
  if (!f || f === "default" || loaded.has(f)) return;
  loaded.add(f);
  const l = document.createElement("link");
  l.rel = "stylesheet";
  l.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@400;600;700;800&display=swap`;
  document.head.appendChild(l);
}

// One slide, rendered identically on the storefront and in the admin preview (mini).
export function SlideView({ s, first = false, mini = false, idx = 0, device = null }) {
  const d = { ...DESIGN, ...(s.design || {}) };
  loadFont(d.font);
  const bg = d.bg_type === "gradient" ? { background: `linear-gradient(120deg, ${d.bg_color}, ${d.bg_color2})` } : { background: d.bg_color };
  const hasImg = d.bg_type === "image" && s.image_url;
  const H = first && !mini ? "h1" : "h2";
  const items = d.align === "center" ? "items-center text-center" : d.align === "right" ? "items-end text-right" : "items-start text-left";
  // translate by the same % as the position keeps the text box inside the slide at every screen size
  const pos = { left: `${d.x}%`, top: `${d.y}%`, transform: `translate(-${d.x}%, -${d.y}%)`, "--mw": `${d.max_width}%`, color: d.text_color,
    fontFamily: d.font !== "default" ? `'${d.font}', sans-serif` : undefined };
  return (
    <div className="absolute inset-0" style={bg}>
      {hasImg && (() => {
        const fit = `absolute inset-0 w-full h-full ${d.image_fit === "contain" ? "object-contain" : "object-cover"}`;
        const alt = d.show_text ? "" : s.heading || "Promotion";
        const lazy = first ? { fetchPriority: "high" } : { loading: "lazy" };
        if (device) return <img src={imgSrc(device === "mobile" && s.mobile_image_url ? s.mobile_image_url : s.image_url)} alt={alt} className={fit} />;
        return (
          <picture>
            {s.mobile_image_url && <source media="(max-width: 639px)" srcSet={imgSrc(s.mobile_image_url)} />}
            <img src={imgSrc(s.image_url)} alt={alt} className={fit} decoding="async" {...lazy} />
          </picture>);
      })()}
      {d.show_text && d.overlay > 0 && <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, rgba(15,23,42,${d.overlay / 100}), rgba(15,23,42,${d.overlay / 200}))` }} />}
      {d.show_text && (
        <div className={`absolute w-full ${mini ? "p-3 max-w-[var(--mw)]" : "px-5 sm:px-10 lg:px-16 py-6 sm:max-w-[var(--mw)]"}`} style={pos}>
          <div className={`flex flex-col ${items} max-w-full`} style={{ marginInline: "auto" }}>
            {s.eyebrow && <span className={`font-mono tracking-[0.3em] ${mini ? "text-[8px]" : "text-[11px] sm:text-xs"}`} style={{ color: d.accent_color }}>{s.eyebrow}</span>}
            <H className={`${mini ? "mt-1" : "mt-3 sm:mt-4"} font-extrabold tracking-tight leading-[1.05] ${SIZES[mini ? "mini" : "full"][d.size]}`}>{s.heading}</H>
            {s.text && <p className={`${mini ? "mt-1 text-[9px]" : "mt-3 sm:mt-5 text-sm sm:text-lg"} opacity-80`}>{s.text}</p>}
            {s.button_label && s.button_link && (mini
              ? <span className="mt-2 inline-block px-2 py-0.5 rounded text-[9px] font-semibold text-slate-900" style={{ background: d.accent_color }}>{s.button_label}</span>
              : <Link to={s.button_link} className="mt-5 sm:mt-6 inline-flex items-center gap-2 px-5 h-11 rounded-md font-semibold text-slate-900 hover:brightness-110 transition-[filter]" style={{ background: d.accent_color }} data-testid={`hero-cta-${idx}`}>
                {s.button_label} <ArrowRight size={16} /></Link>)}
            {s.badges?.length > 0 && !mini && (
              <div className={`mt-6 sm:mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs sm:text-sm ${d.align === "center" ? "justify-center" : d.align === "right" ? "justify-end" : ""}`}>
                {s.badges.map((b, j) => { const Icon = ICONS[j % 3]; return (
                  <div key={j} className="flex items-center gap-2 opacity-90"><span className="grid place-items-center w-8 h-8 rounded-md bg-white/10"><Icon size={16} style={{ color: d.accent_color }} /></span>{b}</div>); })}
              </div>)}
          </div>
        </div>)}
      {!d.show_text && s.button_link && !mini && <Link to={s.button_link} className="absolute inset-0" aria-label={s.heading || s.button_label || "Open promotion"} data-testid={`hero-cta-${idx}`} />}
    </div>
  );
}
